import React, { useEffect, useRef, useState } from "react";
import { WSMessageStatus } from "@veris-health/user-ms/lib/v1/index";
import { SessionWarningTimeout } from "../../constants";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import {
  localizedLogout,
  selectIsLoggedIn,
  selectUserId,
  startConnecting,
  terminateConnection,
} from "../shared/slices/authSlice";
import { SessionModal } from "./SessionModal";
import { unregisterFirebaseTokenAsync } from "../Notifications/notificationsSlice";
import { selectCallStatus } from "../shared/slices/voipSlice";
import { useReadLocalStorage } from "../../hooks/useReadLocalStorage";
import { isFederatedUser } from "../../api/helpers";
import { terminateTokens } from "../../api/utils/localStorage";
import { logoutSSO } from "../../ssoConfig";
import { crossTabLogoutChannel } from "../../crossTabLogoutChannel";
import { isSocketOpen, sendLogoutAndClose } from "../shared/middleware/socketBridge";

interface SessionContainerProps {
  children: React.ReactNode;
}

export const SessionContainer = ({ children }: SessionContainerProps): JSX.Element => {
  const authTokenInLocalStorage = useReadLocalStorage("VERIS_ACCESS_TOKEN");
  const callStatus = useAppSelector(selectCallStatus);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const currentUserId = useAppSelector(selectUserId);
  const [modalOpenState, setModalOpenState] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(0);
  const dispatch = useAppDispatch();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!authTokenInLocalStorage) {
      dispatch(localizedLogout());
    }
  }, [authTokenInLocalStorage]);

  const destroyTimeout = (): void => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const onPrompt = (): void => {
    destroyTimeout();
    setTimeoutCountdown(2 * 60 * 1000);
    setModalOpenState(true);
  };

  const createTimeout = (): void => {
    destroyTimeout();
    if (!callStatus.isOnCall) {
      timeoutRef.current = setTimeout(() => {
        onPrompt();
      }, SessionWarningTimeout * 60 * 1000);
    }
  };

  const handleLogout = async (): Promise<void> => {
    destroyTimeout();
    setModalOpenState(false);
    dispatch(unregisterFirebaseTokenAsync(Number(currentUserId)));

    if (isFederatedUser()) {
      crossTabLogoutChannel.postMessage("LOGOUT");
      if (isSocketOpen()) {
        sendLogoutAndClose({ message: WSMessageStatus.Logout });
      }
      await logoutSSO();
      terminateTokens();
    }
    dispatch(localizedLogout());
  };

  const handleContinueSession = (): void => {
    createTimeout();
    setModalOpenState(false);
  };

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(startConnecting());
    }

    return () => {
      dispatch(terminateConnection());
    };
  }, [isLoggedIn]);

  useEffect(() => {
    const clickHandler = () => {
      createTimeout();
    };

    if (isLoggedIn) {
      createTimeout();
      document.addEventListener("click", clickHandler);
    }

    return () => {
      document.removeEventListener("click", clickHandler);
      destroyTimeout();
    };
  }, [isLoggedIn, callStatus.isOnCall]);

  return (
    <>
      {modalOpenState && (
        <SessionModal
          isOpen={!!isLoggedIn && modalOpenState}
          handleContinueSession={handleContinueSession}
          handleLogout={handleLogout}
          countdownTimer={timeoutCountdown}
        />
      )}
      {children}
    </>
  );
};
